/**
 * IQ REST resource required for server-side stock take submission.
 * Missing from older IQ builds — returns HTTP 404 / "Not Found".
 */
export const STOCK_TAKE_IQ_SUBMIT_API_RESOURCE = 'IQ_API_Submit_Document_Stock_Adjustment';

/**
 * IQ/store-service error codes for stock take processing failures.
 * Extend this map as API documentation adds codes that need specific user guidance.
 */
export enum StockTakeIqProcessingErrorCodeEnum {
  /** Missing REST resource — IQ build without stock take submission API */
  apiResourceNotFound = 404,
}

interface IStockTakeIqErrorDescriptionJson {
  response_code?: number;
  response_message?: string;
}

/**
 * A stock code rejected by IQ during stock take processing, with the rejection reason.
 */
export interface IStockTakeFailedItem {
  code: string;
  reason: string;
}

const STOCK_TAKE_FAILED_ITEM_DEFAULT_REASON = 'Rejected by IQ';

/**
 * Parses `ErrorCode=404` or `response_code` from the JSON `Description` payload.
 */
export function parseStockTakeProcessingErrorCode(errorText: string): number | null {
  const prefixMatch = /ErrorCode=(\d+)/i.exec(errorText);
  if (prefixMatch) {
    return Number(prefixMatch[1]);
  }

  const descMatch = /Description=(\{.*\})/i.exec(errorText);
  if (!descMatch) {
    return null;
  }

  try {
    const parsed = JSON.parse(descMatch[1]) as IStockTakeIqErrorDescriptionJson;
    return typeof parsed.response_code === 'number' ? parsed.response_code : null;
  } catch {
    return null;
  }
}

const STOCK_TAKE_IQ_API_NOT_SUPPORTED_GUIDANCE: string[] = [
  "This store's IQ version does not support stock takes through the API.",
  'The installed IQ build is not new enough to process stock take adjustments.',
  'Upgrade IQ to a version that includes stock take API support, or contact Gallix Support for assistance.',
];

/**
 * User-facing guidance keyed by IQ error code. Add entries as the API documents new codes.
 */
const STOCK_TAKE_PROCESSING_ERROR_GUIDANCE: Partial<Record<number, string[]>> = {
  [StockTakeIqProcessingErrorCodeEnum.apiResourceNotFound]: STOCK_TAKE_IQ_API_NOT_SUPPORTED_GUIDANCE,
};

/**
 * Whether the backend/IQ error indicates the store IQ version does not expose stock take submission.
 */
export function isStockTakeIqApiNotSupportedError(errorText: string | null | undefined): boolean {
  if (!errorText?.trim()) {
    return false;
  }

  const errorCode = parseStockTakeProcessingErrorCode(errorText);
  if (errorCode === StockTakeIqProcessingErrorCodeEnum.apiResourceNotFound) {
    return true;
  }

  const normalized: string = errorText.toLowerCase();
  if (normalized.includes(STOCK_TAKE_IQ_SUBMIT_API_RESOURCE.toLowerCase())) {
    return true;
  }
  return normalized.includes('stock_adjustment') && normalized.includes('not found');
}

/**
 * User-facing modal lines for a stock take processing failure (notification response or stock take errorMsg).
 */
export function buildStockTakeProcessingErrorDescription(
  rawError: string | null | undefined,
): string[] {
  if (!rawError?.trim()) {
    return ['No error details were recorded for this stock take.'];
  }

  const trimmedError = rawError.trim();
  const errorCode = parseStockTakeProcessingErrorCode(trimmedError);
  const guidance =
    errorCode != null ? STOCK_TAKE_PROCESSING_ERROR_GUIDANCE[errorCode] : undefined;

  if (guidance) {
    return [...guidance, '', 'Technical details:', trimmedError];
  }

  if (isStockTakeIqApiNotSupportedError(trimmedError)) {
    return [...STOCK_TAKE_IQ_API_NOT_SUPPORTED_GUIDANCE, '', 'Technical details:', trimmedError];
  }

  return [trimmedError, 'Please contact Gallix Support if you need assistance.'];
}

/**
 * Short inline summary for notification/detail panels when IQ does not support stock takes.
 */
export function stockTakeIqApiNotSupportedSummary(): string {
  return "This store's IQ version is too old to process stock takes. Upgrade IQ or contact Gallix Support.";
}

/**
 * Normalises a reason string from IQ failed-item text.
 */
function cleanStockTakeFailedItemReason(raw: string): string {
  return raw
    .replace(/^_document\s*:\s*/i, '')
    .replace(/^Failed items\s*\(\d+\)\s*:\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extracts a human-readable reason from a structured itemErrors value.
 */
function reasonFromStockTakeItemError(value: unknown): string {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['desc', 'description', 'reason', 'message', 'error'] as const) {
      const candidate = record[key];
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }
  }
  return STOCK_TAKE_FAILED_ITEM_DEFAULT_REASON;
}

/**
 * Item codes and reasons IQ rejected on a stock take submission.
 * Prefers structured `itemErrors`, then `reason [ code ]` groups after `Failed items`.
 */
export function parseStockTakeFailedItems(
  errorText: string | null | undefined,
  itemErrors?: Record<string, unknown> | null,
): IStockTakeFailedItem[] {
  const byCode = new Map<string, string>();

  if (itemErrors) {
    for (const [key, value] of Object.entries(itemErrors)) {
      const code = key.trim();
      if (code.length > 0) {
        byCode.set(code, reasonFromStockTakeItemError(value));
      }
    }
  }

  if (!errorText?.trim()) {
    return [...byCode.entries()].map(([code, reason]) => ({ code, reason }));
  }

  const failedSection = /Failed items\s*\(\d+\)\s*:([\s\S]*)/i.exec(errorText);
  const haystack = failedSection?.[1] ?? errorText;
  const groupMatches = haystack.matchAll(/([^[\]]+?)\s*\[\s*([^\]]+)\s*\]/g);

  for (const match of groupMatches) {
    const rawReason = match[1]?.trim() ?? '';
    const codesInner = match[2]?.trim() ?? '';
    if (!codesInner || /IQ_Error/i.test(codesInner) || /IQ_Error/i.test(rawReason)) {
      continue;
    }

    const reason = cleanStockTakeFailedItemReason(rawReason) || STOCK_TAKE_FAILED_ITEM_DEFAULT_REASON;
    for (const part of codesInner.split(/[,;]/)) {
      const code = part.trim();
      if (code.length > 0 && !/\s/.test(code) && !code.startsWith('_')) {
        const existing = byCode.get(code);
        if (!existing || existing === STOCK_TAKE_FAILED_ITEM_DEFAULT_REASON) {
          byCode.set(code, reason);
        }
      }
    }
  }

  return [...byCode.entries()].map(([code, reason]) => ({ code, reason }));
}

/**
 * Item codes IQ rejected on a stock take submission.
 * Prefers structured `itemErrors` keys, then codes in `[ ... ]` after `Failed items`.
 */
export function parseStockTakeFailedItemCodes(
  errorText: string | null | undefined,
  itemErrors?: Record<string, unknown> | null,
): string[] {
  return parseStockTakeFailedItems(errorText, itemErrors).map((item: IStockTakeFailedItem) => item.code);
}
